/**
 * 활동 로그.
 *
 * "누가 언제 무엇을 바꿨나"에 답한다. 사이니지는 공공장소에 나가는 화면이라
 * 잘못된 콘텐츠가 걸렸을 때 **되돌리는 것보다 원인을 찾는 것**이 먼저다.
 *
 * 변경 전후 스냅샷을 그대로 보여준다. 요약하면 반드시 필요한 필드가 빠진다.
 */
import { useState, type ReactNode } from 'react';
import type { AuditEntry, SignageOneClient } from '../api/client.js';
import { useAsync, useDebounced } from '../hooks.js';
import { Empty, ErrorNote, Loading, Panel, relativeTime, rowAction } from '../components/ui.js';
import { dayEndIso, dayStartIso } from '../components/localDate.js';
import { FilterField, FilterPopover } from '../components/FilterPopover.js';
import { DateField } from '../components/DateField.js';
import { MoreMenu } from '../components/MoreMenu.js';
import { IconButton } from '../components/IconButton.js';
import { ChevronDown, ChevronUp } from 'lucide-react';

/** 서버가 쓰는 동작 이름을 사람 말로. 모르는 값은 그대로 보여준다(새 동작이 늘 생긴다). */
/**
 * 행위자를 사람이 읽는 말로.
 *
 * 저장되는 값은 `user:hyeok@mk-sol.com` · `service:bootstrap-admin` 처럼 **누구인지와
 * 어떤 종류인지를 접두어로 붙인 식별자**다. 그대로 늘어놓으면 화면이 시스템의 말로
 * 채워진다 — 사람이 알고 싶은 것은 "누가" 이지 그 사람이 어떤 주체 종류인지가 아니다.
 *
 * 종류를 버리지는 않는다. 사람이 한 일과 토큰이 한 일은 구분되어야 하고, 그것이
 * 감사로그를 보는 이유 중 하나다 — 다만 이름 뒤에 조용히 붙인다.
 */
export function formatActor(actor: string): { name: string; kind: string | null } {
  const at = actor.indexOf(':');
  if (at === -1) return { name: actor, kind: null };
  const prefix = actor.slice(0, at);
  const name = actor.slice(at + 1) || actor;
  if (prefix === 'user') return { name, kind: null };
  if (prefix === 'service') return { name, kind: '서비스' };
  if (prefix === 'device') return { name, kind: '화면' };
  return { name, kind: prefix };
}

/**
 * 동작 이름이 이미 대상을 말하고 있나.
 *
 * `schedule.update` 는 "스케줄 수정" 인데 뒤에 대상("스케줄")을 또 붙이면
 * "스케줄 수정 스케줄" 이 된다. 사람이 쓰지 않는 말이다.
 */
export function needsResourceLabel(actionLabel: string, resourceLabel: string): boolean {
  return Boolean(resourceLabel) && !actionLabel.includes(resourceLabel);
}

export const ACTION_LABEL: Record<string, string> = {
  'asset.create': '콘텐츠 등록',
  'asset.transcode': '영상 다시 변환',
  'asset.create-template': '템플릿 생성',
  'asset.update': '콘텐츠 수정',
  'asset.update-template': '템플릿 수정',
  'asset.update-html': 'HTML 화면 편집',
  'asset.replace-in-playlists': '플레이리스트에서 콘텐츠 바꿔 넣기',
  'asset.template-entry.register': '템플릿으로 등록',
  'asset.template-entry.update': '템플릿 이름·설명 수정',
  'asset.template-entry.remove': '템플릿 등록 해제',
  'asset.delete': '콘텐츠 삭제',
  'playlist.create': '플레이리스트 생성',
  'playlist.update': '플레이리스트 수정',
  'playlist.delete': '플레이리스트 삭제',
  'display.update': '화면 설정 변경',
  'display.delete': '화면 삭제',
  'display.bulk-update': '화면 일괄 변경',
  'display.location-rename': '위치 이름 변경',
  'display.location-delete': '위치 지우기',
  'display.maintenance-pin': '정비 모드 PIN 변경',
  'display.agent-settings': '원격 설정 변경',
  'display.reboot': '기기 재부팅',
  'display.quit': '원격 앱 종료',
  'display.power-off': '원격 전원 종료',
  'display.exit-maintenance': '원격 정비 모드 해제',
  'display.logs-request': '기기 로그 요청',
  'media.sweep': '남은 미디어 파일 정리',
  'backup.restore': '백업 되살리기(서버 명령)',
  'display.claim': '화면 등록',
  'schedule.create': '스케줄 생성',
  'schedule.update': '스케줄 수정',
  'campaign.create': '캠페인 생성',
  'campaign.update': '캠페인 수정',
  'campaign.delete': '캠페인 삭제',
  'emergency.create': '긴급 오버라이드 발동',
  'emergency.clear': '긴급 오버라이드 해제',
  'display.direct-send': '화면에 바로 보내기',
  'display.direct-send-bulk': '여러 화면에 바로 보내기',
  'display.direct-clear': '바로 보내기 해제',
  'display.pair': '화면 등록',
  'auth.login': '로그인',
  'auth.login-failed': '로그인 실패',
  'auth.logout': '로그아웃',
  'audit.export': '활동 로그 내려받기',
  'report.export': '재생 리포트 내려받기',
  'auth.change-password': '비밀번호 변경',
  'auth.revoke-sessions': '다른 기기 로그아웃',
  'user.create': '사용자 추가',
  'user.update': '사용자 변경',
  'user.delete': '사용자 삭제',
  'user.reset-password': '비밀번호 재설정',
  // 서버 앞의 설정 도구(signageonectl unlock) — 행위자가 `cli` 로 남는다.
  'user.unlock': '로그인 잠금 해제',
  'agent.release.upload': '에이전트 산출물 등록',
  'agent.release.delete': '에이전트 산출물 삭제',
  'agent.install-token.create': '설치 토큰 발급',
  'agent.install-token.revoke': '설치 토큰 폐기',
  'server-package.upload': '서버 설치 파일 등록',
  'server-package.delete': '서버 설치 파일 삭제',
  'delivery.create': '납품 이력 추가',
  'delivery.memo': '납품 메모 수정',
  'token.create': '서비스 토큰 발급',
  'token.revoke': '서비스 토큰 폐기',
  'settings.update': '환경 설정 변경',
  'history.restore': '이전 판으로 되돌리기',
  'settings.brand-font.upload': '사내 글꼴 올리기',
  'settings.brand-font.delete': '사내 글꼴 지우기',
  /*
   * 서버가 남기는데 여기 없던 것들. 표에 없으면 화면은 `display.cache-evict` 같은
   * **내부 id 를 그대로** 보여 준다 — 활동 로그는 "누가 무엇을 바꿨나" 에 답하라고 있는
   * 화면인데, 그 답이 코드에 쓰는 말로 나오면 읽는 사람이 다시 물어야 한다.
   * (`apps/server/src` 의 `action:` 과 이 표를 맞춘 것이다.)
   */
  'asset.bulk-delete': '콘텐츠 일괄 삭제',
  'asset.validity-bulk': '게시 기간 일괄 변경',
  'asset.create-web': '웹 URL 등록',
  'schedule.delete': '스케줄 삭제',
  'display.bulk-delete': '화면 일괄 삭제',
  'display.cache-evict': '화면 캐시 삭제',
  'display.rotate-token': '디바이스 토큰 재발급',
  'group.create': '그룹 생성',
  'group.update': '그룹 변경',
  'group.delete': '그룹 삭제',
  'release.create': '에이전트 릴리스 등록',
  'rollout.update': '롤아웃 변경',
  'backup.download': '백업 내려받기',
  'plugin.module.update': '모듈 상태 변경',
  'plugin.backup-external.update': '백업 외부 보관 설정 변경',
  'plugin.backup-external.run': '백업 외부 보관 실행',
  'plugin.alert-notify.update': '장애 알림 설정 변경',
  'hub.server.create': '통합 관제 서버 등록',
  'hub.server.update': '통합 관제 서버 변경',
  'hub.server.delete': '통합 관제 서버 삭제',
  'hub.display.command': '다른 서버 화면에 원격 명령 (통합 관제)',
  'sites.update': '사업장 구성 변경 (층 이름·지역·사업장·주소 지정)',
  'address-db.import': '주소 DB 넣기 (행정안전부 주소 자료)',
  // 통합 API(호스트 시스템)가 부른 것. 사람이 콘솔에서 한 것과 구분되게 적는다.
  'public.emergency.raise': '긴급 오버라이드 발동 (통합 API)',
  'public.emergency.clear': '긴급 오버라이드 해제 (통합 API)',
  'public.announcement.create': '공지 게시 (통합 API)',
  'public.announcement.clear': '공지 해제 (통합 API)',
};

/** 대상 종류를 사람 말로. */
export const RESOURCE_LABEL: Record<string, string> = {
  asset: '콘텐츠',
  playlist: '플레이리스트',
  display: '화면',
  schedule: '스케줄',
  campaign: '캠페인',
  emergency: '긴급',
  user: '사용자',
  release: '에이전트',
  'install-token': '설치 토큰',
  'service-token': '서비스 토큰',
  'server-package': '서버 설치 파일',
  delivery: '납품 이력',
  plugin: '플러그인',
  'plugin-module': '모듈',
  'hub-server': '통합 관제 서버',
  sites: '사업장 구성',
  settings: '환경 설정',
  'audit-log': '활동 로그',
  report: '재생 리포트',
  storage: '저장소',
  backup: '백업',
};

const RESOURCE_OPTIONS = [
  { value: '', label: '전체 대상' },
  { value: 'asset', label: '콘텐츠' },
  { value: 'playlist', label: '플레이리스트' },
  { value: 'display', label: '화면' },
  { value: 'schedule', label: '스케줄' },
  { value: 'campaign', label: '캠페인' },
  { value: 'emergency', label: '긴급' },
];

export function ActivitiesScreen({ client }: { client: SignageOneClient }): ReactNode {
  const [resourceType, setResourceType] = useState('');
  const [actor, setActor] = useState('');
  /** 기간 — 이 브라우저의 날짜(`YYYY-MM-DD`). 시작일 0시부터 종료일 24시까지. 비우면 그쪽 끝 제한 없음. */
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  // 커서 목록 — 뒤로 가기를 위해 지나온 커서를 쌓아 둔다.
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  /*
   * 수행자는 **입력이 멎은 뒤에** 묻는다. 글자마다 물으면 "user:hyeok" 하나를 치는 동안 활동 로그를
   * 열 번 남짓 조회한다. CSV 도 같은 값을 쓴다 — 화면에 보이는 목록이 이 값으로 받은 것이다.
   */
  const actorQuery = useDebounced(actor.trim(), 300);
  /** 조회와 CSV 가 같은 거르개를 쓴다 — 화면에서 본 것과 내려받은 것이 다르면 안 된다. */
  const filters = {
    ...(resourceType ? { resource_type: resourceType } : {}),
    ...(actorQuery ? { actor: actorQuery } : {}),
    ...(fromDay ? { from: dayStartIso(fromDay) } : {}),
    // 종료일 24시(다음 날 0시) 전까지.
    ...(toDay ? { to: dayEndIso(toDay) } : {}),
  };

  const cursor = cursors[cursors.length - 1];
  const logs = useAsync(
    () =>
      client.auditLogs({
        limit: '50',
        ...(cursor ? { cursor } : {}),
        ...filters,
      }),
    [client, cursor, resourceType, actorQuery, fromDay, toDay],
  );

  const resetPaging = (): void => setCursors([null]);

  return (
    <Panel
      title="활동 로그"
      className="screen-fill"
      help="누가 언제 무엇을 바꿨는지 기록합니다. [보기]를 누르면 변경 전후 값을 확인할 수 있습니다."
      actions={
        <>
          <select
            className="input"
            value={resourceType}
            onChange={(event) => {
              setResourceType(event.target.value);
              resetPaging();
            }}
          >
            {RESOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <input
            className="input input-search"
            placeholder="수행자 (예: user:admin)"
            value={actor}
            onChange={(event) => {
              setActor(event.target.value);
              resetPaging();
            }}
          />
          {/*
            기간과 CSV 는 찾아보거나 감사에 낼 때만 — [기간] 묶음과 [⋯] 뒤로(점진적 공개). 기간이 걸려 있으면 단추에 보인다.
          */}
          <FilterPopover
            label="기간"
            activeCount={fromDay || toDay ? 1 : 0}
            onReset={() => {
              setFromDay('');
              setToDay('');
              resetPaging();
            }}
          >
            <FilterField label="시작일">
              <DateField
                label="시작일"
                clearable
                value={fromDay}
                onChange={(next) => {
                  setFromDay(next);
                  resetPaging();
                }}
              />
            </FilterField>
            <FilterField label="종료일">
              <DateField
                label="종료일"
                clearable
                value={toDay}
                onChange={(next) => {
                  setToDay(next);
                  resetPaging();
                }}
              />
            </FilterField>
          </FilterPopover>
          <button type="button" className="btn" onClick={logs.reload}>
            새로고침
          </button>
          {/* 보안 점검·내부 감사에 내는 파일. 화면의 조건 그대로, 10만 건까지. */}
          <MoreMenu
            size="normal"
            label="활동 로그 더 보기"
            items={[
              {
                label: 'CSV 내보내기',
                title: '지금 조건 그대로, 10만 건까지',
                onSelect: () => {
                  const link = document.createElement('a');
                  link.href = client.auditLogsCsvUrl(filters);
                  link.download = '';
                  link.click();
                },
              },
            ]}
          />
        </>
      }
    >
      {logs.error ? <ErrorNote message={logs.error} /> : null}
      {logs.loading && !logs.data ? <Loading /> : null}

      {logs.data && logs.data.items.length === 0 ? (
        <Empty
          title="기록이 없습니다"
          hint="대상·수행자 조건을 변경해 보세요."
        />
      ) : null}

      {logs.data && logs.data.items.length > 0 ? (
        <>
          <div className="table-scroll">
            <table className="table min-w-[42rem]">
              <thead>
                <tr>
                  <th>시각</th>
                  <th>동작</th>
                  <th>대상</th>
                  <th>수행자</th>
                  <th className="col-actions">변경</th>
                </tr>
              </thead>
              <tbody>
                {logs.data.items.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
            <p className="hint">
              {logs.data.items.length}건 · {cursors.length}쪽
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn"
                disabled={cursors.length === 1}
                onClick={() => setCursors((stack) => stack.slice(0, -1))}
              >
                이전
              </button>
              <button
                type="button"
                className="btn"
                disabled={!logs.data.next_cursor}
                onClick={() =>
                  setCursors((stack) => [...stack, logs.data?.next_cursor ?? null])
                }
              >
                다음
              </button>
            </div>
          </div>
        </>
      ) : null}
    </Panel>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }): ReactNode {
  const [open, setOpen] = useState(false);
  const hasDiff = entry.before !== null || entry.after !== null;

  return (
    <>
      {/* 바뀐 내용이 있는 줄만 펼칠 수 있다 — 키보드로도(`rowAction`). */}
      <tr {...(hasDiff ? { ...rowAction(() => setOpen((prev) => !prev)), 'aria-expanded': open } : {})}>
        <td className="cell-muted whitespace-nowrap">
          <span title={new Date(entry.created_at).toLocaleString('ko-KR')}>
            {relativeTime(entry.created_at)}
          </span>
          {/*
            터치 기기에는 title 이 뜨지 않는다. 감사로그에서 정확한 시각은 빠질 수 없는
            정보라, 마우스가 없는 기기에서는 상대 시각 아래에 그대로 적는다.
          */}
          <span className="hint hidden [@media(hover:none)]:block">
            {new Date(entry.created_at).toLocaleString('ko-KR')}
          </span>
        </td>
        <td className="cell-strong">{ACTION_LABEL[entry.action] ?? entry.action}</td>
        <td>
          <span className="badge badge-neutral">{entry.resource_type}</span>
          <span className="mono">{entry.resource_id}</span>
        </td>
        <td className="cell-muted">
          {formatActor(entry.actor).name}
          {formatActor(entry.actor).kind ? (
            <span className="hint hint-inline">{formatActor(entry.actor).kind}</span>
          ) : null}
          {entry.actor_issuer ? <span className="hint hint-inline">{entry.actor_issuer}</span> : null}
        </td>
        <td className="col-actions">
          {hasDiff ? (
            /* 줄을 누르면 펼친다(`rowAction`) — 단추는 그 자리를 알리는 표시라 따로 동작하지 않는다. */
            <IconButton icon={open ? ChevronUp : ChevronDown} label={open ? '접기' : '변경 보기'} tabIndex={-1} />
          ) : (
            <span className="hint">—</span>
          )}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={5} className="bg-slate-50">
            {/*
              칸 폭을 0 부터 잡는다. 기본 격자 칸은 내용만큼 늘어나서, JSON 한 줄이 길면
              스냅샷 상자 안에서 밀리는 대신 표 전체를 그 길이로 넓혀 버린다.
            */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Snapshot title="변경 전" value={entry.before} />
              <Snapshot title="변경 후" value={entry.after} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function Snapshot({ title, value }: { title: string; value: unknown }): ReactNode {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-slate-400">{title}</p>
      <pre className="max-h-64 overflow-auto rounded-lg border border-slate-200 bg-white p-3 font-mono text-[11.5px] leading-relaxed text-slate-700">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
